'use client';

import { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { TutorialSection, TutorialLesson } from '@/types/tutorial';
import { tutorialService } from '@/services/tutorialService';
import { MarkdownRenderer } from '@/components/chat/markdown/MarkdownRenderer';
import { Check, Share2 } from 'lucide-react';
import VideoPlayer from './VideoPlayer';
import Link from 'next/link';
import Image from 'next/image';

interface CourseDetailContainerProps {
    sectionId: string;
}

// Helper to get video URL from lesson (handles both camelCase and snake_case)
const getVideoUrl = (lesson: TutorialLesson): string | undefined => {
    return lesson.videoUrl || lesson.video_url;
};

// Helper to get slug from lesson (handles both camelCase and snake_case)
const getLessonSlug = (lesson: TutorialLesson): string | undefined => {
    return (lesson as any).slug;
};

const CourseDetailContainer: FC<CourseDetailContainerProps> = ({ sectionId }) => {
    const searchParams = useSearchParams() ?? new URLSearchParams();
    const router = useRouter();
    const [section, setSection] = useState<TutorialSection | null>(null);
    const [activeLesson, setActiveLesson] = useState<TutorialLesson | null>(null);
    const [loading, setLoading] = useState(true);
    const [playRequest, setPlayRequest] = useState(0);
    const [shareStatus, setShareStatus] = useState<'idle' | 'copied' | 'error'>('idle');
    const playerSectionRef = useRef<HTMLElement>(null);
    const shareStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Track auto-detected durations by lesson ID
    const [videoDurations, setVideoDurations] = useState<Record<string, string>>({});

    useEffect(() => {
        const fetchSection = async () => {
            try {
                const data = await tutorialService.getSectionById(sectionId);
                setSection(data);

                // Check for lesson query param to deep-link to specific lesson
                const lessonSlug = searchParams.get('lesson');
                let selectedLesson: TutorialLesson | null = null;

                if (lessonSlug && data?.lessons) {
                    selectedLesson = data.lessons.find(
                        (l: any) => l.slug === lessonSlug
                    ) || null;
                }

                // Default to first lesson if no match or no param
                if (!selectedLesson && data?.lessons && data.lessons.length > 0) {
                    selectedLesson = data.lessons[0];
                }

                setActiveLesson(selectedLesson);
            } catch (error) {
                console.error('Failed to load section', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSection();
    }, [sectionId, searchParams]);

    // Handle lesson selection and update URL for shareability
    const handleLessonSelect = useCallback((lesson: TutorialLesson) => {
        setActiveLesson(lesson);
        setPlayRequest((request) => request + 1);
        setShareStatus('idle');
        if (shareStatusTimeoutRef.current) {
            clearTimeout(shareStatusTimeoutRef.current);
        }
        const slug = getLessonSlug(lesson);
        if (slug) {
            // Update URL without full page reload
            const url = new URL(window.location.href);
            url.searchParams.set('lesson', slug);
            router.replace(url.pathname + url.search, { scroll: false });
        }

        if (window.matchMedia('(max-width: 1023px)').matches) {
            window.requestAnimationFrame(() => {
                playerSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            });
        }
    }, [router]);

    useEffect(() => () => {
        if (shareStatusTimeoutRef.current) {
            clearTimeout(shareStatusTimeoutRef.current);
        }
    }, []);

    const showShareStatus = useCallback((status: 'copied' | 'error') => {
        setShareStatus(status);
        if (shareStatusTimeoutRef.current) {
            clearTimeout(shareStatusTimeoutRef.current);
        }
        shareStatusTimeoutRef.current = setTimeout(() => setShareStatus('idle'), 2000);
    }, []);

    const copyShareUrl = useCallback(async (url: string) => {
        if (!navigator.clipboard?.writeText) {
            throw new Error('Clipboard API is unavailable');
        }
        await navigator.clipboard.writeText(url);
        showShareStatus('copied');
    }, [showShareStatus]);

    const handleShare = useCallback(async () => {
        if (!activeLesson) return;

        const url = new URL(window.location.href);
        const slug = getLessonSlug(activeLesson);
        if (slug) {
            url.searchParams.set('lesson', slug);
        }

        const shareUrl = url.toString();
        const shareData = {
            title: activeLesson.title,
            text: `Watch "${activeLesson.title}" from ${section?.title || 'MarketBetter Playbooks'}.`,
            url: shareUrl,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
                return;
            }
            await copyShareUrl(shareUrl);
        } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
                return;
            }

            try {
                await copyShareUrl(shareUrl);
            } catch {
                showShareStatus('error');
            }
        }
    }, [activeLesson, copyShareUrl, section?.title, showShareStatus]);

    // Callback to update duration when video metadata loads
    const handleDurationLoaded = useCallback((duration: string) => {
        if (activeLesson) {
            setVideoDurations(prev => ({
                ...prev,
                [activeLesson.id]: duration
            }));
        }
    }, [activeLesson]);

    // Preload durations for all videos
    useEffect(() => {
        if (!section?.lessons) return;

        const formatDuration = (seconds: number): string => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };

        section.lessons.forEach(lesson => {
            const videoUrl = lesson.videoUrl || lesson.video_url;
            if (!videoUrl || videoDurations[lesson.id]) return;

            const video = document.createElement('video');
            video.preload = 'metadata';
            video.src = videoUrl;
            video.onloadedmetadata = () => {
                if (video.duration && !isNaN(video.duration)) {
                    setVideoDurations(prev => ({
                        ...prev,
                        [lesson.id]: formatDuration(video.duration)
                    }));
                }
            };
        });
    }, [section?.lessons, videoDurations]);

    // Get display duration for a lesson (auto-detected or fallback)
    const getLessonDuration = (lesson: TutorialLesson): string => {
        return videoDurations[lesson.id] || lesson.duration || '--:--';
    };

    if (loading) return <div className="min-h-screen bg-white flex items-center justify-center">Loading...</div>;
    if (!section) return <div className="min-h-screen bg-white flex items-center justify-center">Course not found</div>;

    return (
        <div className="bg-white min-h-screen">
            {/* Hero Section - boxed to match content width */}
            <div className='max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 pt-8'>
                <div className="bg-[#E6EDFF]/50 border border-indigo-100/50 rounded-2xl px-5 py-7 sm:px-8 sm:py-12">
                    {/* Breadcrumbs */}
                    <nav className="flex text-sm font-medium text-gray-500 mb-6">
                        <Link href="/playbooks" className="hover:text-gray-900 transition-colors">Playbooks</Link>
                        <span className="mx-2 text-gray-400">{'>'}</span>
                        <span className="text-indigo-600 font-semibold">{section.category || 'Sales'}</span>
                    </nav>

                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{section.title}</h1>
                    <p className="text-lg sm:text-xl text-gray-600 max-w-3xl leading-relaxed">
                        {section.description}
                    </p>
                </div>
            </div>

            <div className='max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12'>
                <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                    {/* Left Sidebar: Table of Contents */}
                    <aside className="w-full lg:w-80 flex-shrink-0" aria-label="Course lessons">
                        <div className="sticky top-8">
                            <div className="pb-4 mb-2">
                                <h3 className="font-semibold text-gray-500 uppercase tracking-wider text-sm">Table of contents</h3>
                            </div>

                            <div className="relative">
                                {/* Vertical fading line */}
                                <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-gray-200 to-transparent"></div>

                                <div className="flex flex-col">
                                    {section.lessons?.map((lesson) => (
                                        <button
                                            key={lesson.id}
                                            onClick={() => handleLessonSelect(lesson)}
                                            aria-current={activeLesson?.id === lesson.id ? 'true' : undefined}
                                            aria-label={`Play ${lesson.title}`}
                                            className={`
                                                group w-full text-left pl-6 pr-4 py-4 flex items-start gap-3 border-b border-gray-100 last:border-0 transition-all
                                                ${activeLesson?.id === lesson.id ? 'bg-indigo-50/30' : 'hover:bg-gray-50/50'}
                                            `}
                                        >
                                            <div className="flex-shrink-0 mt-0.5">
                                                <Image
                                                    src="/icon-play.svg"
                                                    alt=""
                                                    width={16}
                                                    height={16}
                                                />
                                            </div>
                                            <div>
                                                <span className={`text-[15px] font-medium block transition-colors ${activeLesson?.id === lesson.id ? 'text-indigo-700' : 'text-gray-700 group-hover:text-gray-900'}`}>
                                                    {lesson.title}
                                                </span>
                                                <span className="text-xs text-gray-400 mt-1 block">{getLessonDuration(lesson)}</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content: Video & Details */}
                    <section
                        ref={playerSectionRef}
                        aria-label="Lesson player"
                        className="order-first flex-1 min-w-0 scroll-mt-24 lg:order-none"
                    >
                        {activeLesson ? (
                            <div className="space-y-8">
                                {/* Video Player Card */}
                                <div className="rounded-2xl overflow-hidden shadow-lg">
                                    <VideoPlayer
                                        videoUrl={getVideoUrl(activeLesson)}
                                        title={activeLesson.title}
                                        onDurationLoaded={handleDurationLoaded}
                                        playRequest={playRequest}
                                    />
                                </div>

                                <div>
                                    <div className="mb-6 flex items-start justify-between gap-4">
                                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">{activeLesson.title}</h2>
                                        <button
                                            type="button"
                                            onClick={handleShare}
                                            className="inline-flex min-h-10 flex-shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                                            aria-label={`Share ${activeLesson.title}`}
                                        >
                                            {shareStatus === 'copied' ? (
                                                <Check className="h-4 w-4 text-green-600" aria-hidden="true" />
                                            ) : (
                                                <Share2 className="h-4 w-4" aria-hidden="true" />
                                            )}
                                            <span>
                                                {shareStatus === 'copied'
                                                    ? 'Link copied'
                                                    : shareStatus === 'error'
                                                        ? 'Try again'
                                                        : 'Share'}
                                            </span>
                                        </button>
                                    </div>
                                    {activeLesson.description && (
                                        <MarkdownRenderer
                                            content={activeLesson.description}
                                            className="prose prose-lg prose-gray max-w-none"
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-20 bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                <p className="text-gray-500">Select a lesson to start learning</p>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default CourseDetailContainer;
